import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { UploadDialog } from "@/components/UploadDialog";
import { MediaLightbox } from "@/components/MediaLightbox";
import { OwnerUploadRow } from "@/components/OwnerUploadRow";
import { OwnerMediaCard } from "@/components/OwnerMediaCard";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ownerRowState } from "@/lib/owner";
import { Plus, Loader2, Settings } from "lucide-react";

const RECENT = 10;
const POLL_MS = 4000;

// /owner — the ONE owner workflow (phone and desktop): Upload → processing → finished media card (title · description · X post ·
// Copy for X · chat) → recent list. Authorization comes from GET /auth/me (`is_master_owner`), never from the frontend.
export default function OwnerMobile() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center" data-testid="owner-loading"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_master_owner) return <Navigate to="/browse" replace />;   // owner page is for the site owner only (ADMIN_EMAILS-only admins use the desktop tools)
  return <OwnerHome />;
}

const OwnerHome = () => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [editId, setEditId] = useState(null);
  const [focusId, setFocusId] = useState(null);
  const gen = useRef(0);

  const load = useCallback(async () => {
    const g = ++gen.current;
    try {
      const { data } = await api.get("/my/media", { params: { limit: RECENT } });
      if (g === gen.current) setRows(data);
    } catch (e) { if (g === gen.current && rows === null) setRows([]); }
  }, [rows]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // Poll the stored records only while something is still processing / captioning (read-only).
  const pending = (rows || []).some((m) => ["processing", "captioning"].includes(ownerRowState(m).key) || m.reprocess?.status === "processing");
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => { if (document.visibilityState !== "hidden") load(); }, POLL_MS);
    return () => clearInterval(t);
  }, [pending, load]);

  const list = rows || [];
  const focus = list.find((m) => m.id === focusId) || list[0] || null;   // newest upload is the finished-media card by default
  const onUploaded = (ids) => { if (ids?.length) setFocusId(ids[0]); load(); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const pick = (id) => { setFocusId(id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const next = (id) => {
    const i = list.findIndex((m) => m.id === id);
    const after = list.slice(i + 1).find((m) => ownerRowState(m).key === "ready") || list[i + 1];
    if (after) pick(after.id);
  };

  return (
    <div className="min-h-screen relative z-10" data-testid="owner-home">
      <Navbar />
      {/* Phone: one column (upload → focused card → recent). Desktop (lg+): two columns — focused card left, upload + recent list right. */}
      <main className="px-4 pt-5 pb-24 mx-auto max-w-lg md:max-w-2xl lg:max-w-6xl lg:px-8 lg:pt-8 space-y-6 lg:space-y-0 lg:grid lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-8 lg:items-start" data-testid="owner-main">
        <button
          type="button"
          data-testid="owner-upload-btn"
          onClick={() => setUploadOpen(true)}
          className="w-full h-24 rounded-2xl bg-primary text-primary-foreground text-2xl font-semibold tracking-wide flex items-center justify-center gap-3 shadow-lg shadow-primary/30 active:scale-[0.98] transition-transform lg:order-2 lg:h-20 lg:text-xl"
        >
          <Plus className="w-8 h-8" strokeWidth={3} /> UPLOAD
        </button>

        <div className="lg:order-1 lg:row-span-2" data-testid="owner-focus-column">
          {focus ? <OwnerMediaCard media={focus} onEdit={() => setEditId(focus.id)} onChanged={load} />
                 : <div className="hidden lg:flex h-64 items-center justify-center rounded-2xl border border-dashed border-white/10 text-sm text-muted-foreground">Pick an upload on the right to review it here.</div>}
        </div>

        <section className="space-y-2 lg:order-3 lg:mt-6">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Recent uploads</h2>
            <Link to="/owner/settings" className="text-[11px] text-muted-foreground inline-flex items-center gap-1 hover:text-foreground" data-testid="owner-settings-link"><Settings className="w-3 h-3" /> Settings</Link>
            {pending && <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1" data-testid="owner-polling"><Loader2 className="w-3 h-3 animate-spin" /> updating</span>}
          </div>
          {rows === null ? (
            <div className="py-10 text-center text-muted-foreground text-sm" data-testid="owner-recent-loading"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm" data-testid="owner-recent-empty">Nothing yet — tap Upload.</div>
          ) : (
            <div className="space-y-2" data-testid="owner-recent-list">
              {list.map((m) => (
                <OwnerUploadRow key={m.id} m={m} focused={m.id === (focus && focus.id)} onPick={() => pick(m.id)} onEdit={() => setEditId(m.id)} onNext={() => next(m.id)} onChanged={load} />
              ))}
            </div>
          )}
        </section>
      </main>

      <UploadDialog open={uploadOpen} quick onClose={() => setUploadOpen(false)} onUploaded={onUploaded} />
      <MediaLightbox mediaId={editId} open={!!editId} onClose={() => setEditId(null)} onChanged={load} />
    </div>
  );
};
